import http from "../../core/services/http.service";
import {SOURCE_SETTLEMENT_PAYMENTS_TABLE, SOURCE_SETTLEMENTS_TABLE} from "@/modules/constants";
import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
import {store} from "@/plugins/vuex";

const SettlementService = {
  createAndRetrieveChecksPdf(data) {
    return http.post('/api/v1/settlements/checks-pdf-data/', data)
  },
  createAndRetrieveBacsPayments(data) {
    return http.post('/api/v1/settlements/bacs-batch-payments/', data)
  },
  getSettlementVendorData(params) {
    return http.get(`/api/v1/settlements/customers/`, {params});
  },
  getVendorData(params) {
    return http.get(`api/v1/settlements/customers/${params.customer_uuid}/`, {
      params: {
        ...params,
        expand: [
          'receipts',
          'settlements.financial_items.lot.inventory.receipt.branch',
          'settlements.miscellaneous_items.lot',
          'settlements.miscellaneous_items.inventory',
          'settlements.miscellaneous_items.receipt',
          'settlements.settlement_payments.payment',
          'settlements.currency',
          'settlements.paid_amount',
          'settlements.outstanding_amount',
          'settlements.settlement_charges.charge',
          'settlements.settlement_billing_address.country',
        ].join(',')
      }
    });
  },
  getSettlements(params) {
    return http.get('/api/v1/settlements/compact-list/', {params});
  },
  getSettlement(uuid, params) {
    return http.get(`/api/v1/settlements/${uuid}/`, {params});
  },
  getSettlementsFull(params) {
    return http.get(`/api/v1/settlements/`, {params});
  },
  createSettlement(data) {
    return http.post('/api/v1/settlements/', data);
  },
  updateSettlement(id, data, expand = '') {
    return http.put(`/api/v1/settlements/${id}/?expand=${expand}`, data);
  },
  cancelSettlement(id, expand = '') {
    return http.post(`/api/v1/settlements/${id}/cancel/?expand=${expand}`);
  },
  getSettlementPdfData(uuid) {
    return http.get(`/api/v1/settlements/${uuid}/pdf-data/`);
  },
  getSettlementCheckPdfData(uuid, merged) {
    return http.get(`api/v1/settlements/check-pdf-data/${uuid}/?merged=${merged}`);
  },
  generateMergedPdfForSettlements(data) {
    return http.post('/api/v1/settlements/generate-merged-pdf/', data);
  },
  downloadSettlementPdfs(data) {
    return http.post('/api/v1/settlements/download-pdfs/', data);
  },
  batchCreateSettlements(data) {
    return http.post(`/api/v1/settlements/batch/`, data);
  },

  createPayment(data) {
    return http.post(`/api/v1/settlements/payments/`, data);
  },
  updatePayment(uuid, data, expand = '') {
    return http.put(`/api/v1/settlements/payments/${uuid}/?expand=${expand}`, data);
  },
  getPayment(id, params) {
    return http.get(`/api/v1/payments/${id}/`, {params});
  },
  removeSettlementPayment(uuid) {
    return http.delete(`/api/v1/settlements/payment/${uuid}/`)
  },
  sendSettlementNotifications(data) {
    return http.post(`/api/v1/settlements/notify/`, data);
  },
  sendIntroductorySettlementNotifications(data) {
    return http.post(`/api/v1/settlements/notify-introductory/`, data);
  },
  sendSettlementPaymentNotifications(data) {
    return http.post(`/api/v1/settlements/notify-payment-notification/`, data);
  },
  calculateData(data, expand) {
    return http.post(`/api/v1/settlements/calculate/?expand=${expand}`, data);
  },
  getSettlementsTotalAmounts(params = null) {
    return http.get('/api/v1/dashboard/settlements/', {params});
  },
  getUnsettledFinancialItemsByCustomerForAuction(customerUuid, auctionUuid, expand) {
    return http.get(`/api/v1/finances/merged-financial-items/?intermediary_customer_uuid=${customerUuid}&auction_uuid=${auctionUuid}&is_settled_introductory=false&is_manual_introductory=false&expand=${expand}&limit=500`);
  },
  getUnsettledFinancialItemsByCustomerForManual(customerUuid, expand) {
    return http.get(`/api/v1/finances/merged-financial-items/?intermediary_customer_uuid=${customerUuid}&is_settled_introductory=false&is_manual_introductory=true&expand=${expand}&limit=500`);
  },
  getAllFinancialItemsByCustomerForAuction(customerUuid, auctionUuid, expand) {
    return http.get(`/api/v1/finances/merged-financial-items/?intermediary_customer_uuid=${customerUuid}&auction_uuid=${auctionUuid}&is_manual_introductory=false&expand=${expand}&limit=500`);
  },
  getAllFinancialItemsByCustomerForManual(customerUuid, expand) {
    return http.get(`/api/v1/finances/merged-financial-items/?intermediary_customer_uuid=${customerUuid}&is_manual_introductory=true&expand=${expand}&limit=500`);
  },
  createDebitNote(data) {
    return http.post(`/api/v1/settlements/debit-note/`, data);
  },
  updateDebitNote(data) {
    return http.patch(`/api/v1/settlements/${data.settlement_uuid}/`, data);
  },
  sendDebitNoteNotification(data) {
    return http.post(`/api/v1/settlements/debit-note/notify/`, data);
  },
  partialUpdatePayment(uuid, data) {
    return http.patch(`/api/v1/payments/${uuid}/`, data);
  },
  calculateVendorCharge(params) {
    return http.post(`/api/v1/settlements/calculate-vendor-charge/`, params);
  },
  calculateMultipleVendorCharges(params) {
    return http.post(`/api/v1/settlements/calculate-multiple-vendor-charges/`, params)
  },
  calculateDropdownVendorCharges(params) {
    return http.post(`/api/v1/settlements/calculate-dropdown-vendor-charges/`, params)
  },
  getSettlementPaymentsTotalAmounts(params = null) {
    return http.get('/api/v1/dashboard/settlement-payments/', {params});
  },
  async onEditPayment(vm, settlementPayment, source, settlement) {
    // check for batch payment validity
    if (settlementPayment.payment.batch_payment && settlementPayment.payment.batch_payment.is_approved) {
      let msg = vm.$i18n.t(
        'This payment is part of an approved batch payment that has not yet been exported. Editing it may affect the batch payment. If you proceed, the batch payment will be updated automatically to reflect these changes. Batch ID: {id}. Do you want to proceed?',
        {id: settlementPayment.payment.batch_payment.payment_no}
      )

      let success = await vm.$confirm(msg)
      if (!success) {
        return false
      }
    }

    vm.showPaymentModal({
      object: {},
      source: source,
      settlements: [settlement],
      type: settlementPayment.payment.type
    });
    let response = await SettlementService.getPayment(settlementPayment.payment.uuid, {expand: 'settlement_payments.settlement.currency,settlement_payments.payment.beneficiary_relation.related_customer'});
    if (response.success) {
      const data = response.body;
      if (data.settlement_payments.length > 0 && data.settlement_payments[0].payment.beneficiary_relation) {
        data.beneficiary_relation_uuid = data.settlement_payments[0].payment.beneficiary_relation.uuid;
      }
      vm.updatePaymentModalObject({object: data});
    }
  },
  async onDeletePayment(vm, settlementPayment, source) {
    let msg = vm.$i18n.t("Are you sure you want to delete following payment?")

    if (settlementPayment.payment.batch_payment && settlementPayment.payment.batch_payment.is_approved) {
      msg = vm.$i18n.t(
        'This payment is part of an approved batch payment that has not yet been exported. Deleting it may affect the batch payment. If you proceed, the batch payment will be updated automatically to reflect these changes. Batch ID: {id}. Do you want to proceed?',
        {id: settlementPayment.payment.batch_payment.payment_no}
      )
    }

    let success = await vm.$confirm(
      msg,
      vm.$i18n.t('Payment was successfully deleted'),
      SettlementService.removeSettlementPayment,
      settlementPayment.uuid,
    );

    if (success) {
      if (source === SOURCE_SETTLEMENTS_TABLE) {
        await vm.getTableData();
        await vm.updatePaymentListModalSettlement();
        if (vm.settlement.settlement_payments.length === 0) {
          vm.hidePaymentListModal();
        }
      } else if (source === SOURCE_SETTLEMENT_PAYMENTS_TABLE) {
        await vm.getTableData();
      }
    }
  },
  async viewSettlementPdf(vm, settlement) {
    vm.$evBus.$emit(ON_PDF_MODAL_OPEN, {
      title: vm.$i18n.t('Settlement {settlement_no}', {settlement_no: settlement.settlement_no})
    });
    let response = await SettlementService.getSettlementPdfData(settlement.uuid);
    if (response.success) {
      vm.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
        ...response.body,
        filename: store.state.global.settlementLabels.pdf_filename
      })
    } else {
      vm.$evBus.$emit(ON_PDF_MODAL_CLOSE);
      let errorMessage = "";
      if (response.body.non_field_errors) {
        errorMessage = response.body.non_field_errors[0];
      } else {
        errorMessage = response.body;
      }
      vm.$errorDialog(errorMessage);
    }
  },
  processBeneficiaries(settlement) {
    const financialItems = settlement.financial_items || [];

    let filterBeneficiaries = (data, receipt_no) => {
      return data.filter(item => item.type === "beneficiary").map(item => ({...item, receipt_no}));
    }

    let beneficiaries = [];
    let receiptBeneficiariesFlag = [];

    financialItems.forEach(item => {
      if (item.receipt) {
        const tmpRelations = item.receipt.relations;
        if (tmpRelations && !receiptBeneficiariesFlag.includes(item.receipt.receipt_no)) {
          receiptBeneficiariesFlag.push(item.receipt.receipt_no)
          beneficiaries.push(...filterBeneficiaries(tmpRelations, item.receipt.receipt_no));
        }
      }
    });
    return beneficiaries.length > 0 ? beneficiaries : null;
  },
  async getBeneficiariesBySettlement(settlement) {
    const settlementResponse = await SettlementService.getSettlement(settlement.uuid, {
      expand: [
        'financial_items.receipt.relations.related_customer',
      ].join(',')
    });
    if (settlementResponse.success) {
      return SettlementService.processBeneficiaries(settlementResponse.body)
    }
    return null
  },
  async getBeneficiariesMapped(uuids) {
    const settlementResponse = await SettlementService.getSettlementsFull( {
      uuids: uuids.join(','),
      expand: 'financial_items.receipt.relations.related_customer'
    });

    let returnData = []
    if (settlementResponse.success) {
      for (let settlement of settlementResponse.body.results) {
        returnData[settlement.uuid] = SettlementService.processBeneficiaries(settlement)
      }
    }

    return returnData
  },
  updateSettlementExtraInfo(data) {
    return http.patch(`/api/v1/settlements/edit-settlement-extra-info/`, data);
  },
  approveSettlements(data) {
  return http.patch(`/api/v1/settlements/batch/approve-settlement/`, data);
}
};

export default SettlementService;
